import React, { useEffect, useState, useRef } from "react";
import { DashboardLayout } from "components/layout/components/DashboardLayout";
import { Navbar } from "components/layout/components/Navbar";
import { Button, Form, AutoComplete } from "antd";
import {
  CheckOutlined,
  HomeOutlined,
  MinusCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { routePath } from "config/routeMgt/routePath";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { AppButton } from "components/button/AppButton";
import { useJobFormStore } from "../hooks/useJobFormStore";
import WarningModal from "components/layout/components/WarningModal";
import { jobsCreationPath } from "utils/formHelpers/routeHelper";
import { useGetSkillsByJobTitle } from "../hooks/useCacheData";
import { useQueryClient } from "react-query";

const cleanJobDescription = (text: string): string => {
  if (!text) return "";
  const plainText = text.replace(/<(.|\n)*?>/g, "").trim();
  const sentences = plainText.split(/\.\s+/).filter((s) => s.trim().length > 0);
  const uniqueSentences: string[] = [];
  const seen = new Set();

  for (const sentence of sentences) {
    const normalized = sentence.trim().toLowerCase().substring(0, 30);
    if (!seen.has(normalized)) {
      seen.add(normalized);
      uniqueSentences.push(sentence.trim());
    }
  }

  let cleaned = uniqueSentences.join(". ");
  if (cleaned && !cleaned.endsWith(".")) {
    cleaned += ".";
  }
  return cleaned;
};

export const JobsCreation2 = () => {
  const [form] = Form.useForm();
  const { token } = useGetUserInfo();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    skills,
    jobTitleId,
    updateStep2,
    setCurrentStep,
    resetForm,
    description,
  } = useJobFormStore();

  const [isCancelModalVisible, setCancelModalVisible] = useState(false);
  const queryClient = useQueryClient();

  const [skillsManuallyEdited, setSkillsManuallyEdited] = useState(false);
  const [lastProcessedJobTitleId, setLastProcessedJobTitleId] = useState<
    number | null
  >(null);

  const initialLoadDone = useRef(false);

  const { data: jobTitleSkills } = useGetSkillsByJobTitle(jobTitleId, token, {
    initialData: () =>
      queryClient.getQueryData(["skills-by-jobtitle", jobTitleId]),
  });

  useEffect(() => {
    setCurrentStep(2);
  }, [setCurrentStep]);

  useEffect(() => {
    if (initialLoadDone.current && skills && skills.length > 0) {
      form.setFieldsValue({
        skills: skills,
      });
      return;
    }

    if (skills && skills.length > 0) {
      form.setFieldsValue({
        skills: skills,
      });
      initialLoadDone.current = true;
      return;
    }

    // Fall back to job title skills if no skills in store
    if (jobTitleId && jobTitleSkills && jobTitleSkills.length > 0) {
      const suggestedSkills = jobTitleSkills
        .slice(0, 5)
        .map((skill: any) => ({ name: skill.name }));
      updateStep2({ skills: suggestedSkills });
      form.setFieldsValue({ skills: suggestedSkills });
      initialLoadDone.current = true;
      return;
    }

    form.setFieldsValue({ skills: [] });
    initialLoadDone.current = true;
  }, [form, skills, jobTitleId, jobTitleSkills, updateStep2]);

  useEffect(() => {
    if (skillsManuallyEdited) return;

    if (!jobTitleId || !jobTitleSkills || jobTitleSkills.length === 0) return;

    if (jobTitleId === lastProcessedJobTitleId) return;

    setLastProcessedJobTitleId(jobTitleId);

    const currentSkills = form.getFieldValue("skills") || [];
    const suggestedSkills = jobTitleSkills
      .slice(0, 5)
      .map((skill: any) => ({ name: skill.name }));

    const currentNames = currentSkills
      .map((s: any) => s.name)
      .sort()
      .join(",");
    const suggestedNames = suggestedSkills
      .map((s: any) => s.name)
      .sort()
      .join(",");

    if (currentNames !== suggestedNames) {
      updateStep2({ skills: suggestedSkills });
      form.setFieldsValue({ skills: suggestedSkills });
    }
  }, [
    jobTitleId,
    jobTitleSkills,
    lastProcessedJobTitleId,
    form,
    updateStep2,
    skillsManuallyEdited,
  ]);

  const handleSkillChange = (name: number, value: string) => {
    setSkillsManuallyEdited(true);

    const currentSkills = form.getFieldValue("skills") || [];
    const updatedSkills = [...currentSkills];
    if (updatedSkills[name]) {
      updatedSkills[name] = { name: value };
    }
    updateStep2({ skills: updatedSkills });
  };

  const handleSubmit = (data: any) => {
    const currentSkills = data.skills || [];
    updateStep2({
      skills: currentSkills,
      description: description,
    });

    const { jobId } = useJobFormStore.getState();
    navigate(routePath.jobsPreview);
  };

  const handleBack = () => {
    const currentSkills = form.getFieldValue("skills") || [];

    const store = useJobFormStore.getState();
    if (JSON.stringify(currentSkills) !== JSON.stringify(store.skills)) {
      updateStep2({ skills: currentSkills });
    }

    const returnTo = (location.state as { returnTo?: string } | null)?.returnTo;
    if (returnTo) {
      navigate(returnTo);
      return;
    }

    const routeKey =
      store.isEditing && store.jobId ? `${store.jobId}___edit` : store.jobId;
    navigate(jobsCreationPath(routeKey));
  };

  const handleCancel = () => {
    setCancelModalVisible(true);
  };

  const handleConfirmCancel = () => {
    resetForm();
    sessionStorage.removeItem("jobCreationFormData");
    sessionStorage.removeItem("jobCreationSkillsData");
    navigate(routePath.jobs);
  };

  return (
    <DashboardLayout>
      <Navbar title="Add Job" description="View and Manage Jobs" />

      <div>
        <h3 className="text-sm flex items-center gap-1 text-gray-700">
          <HomeOutlined className="text-gray-500" />
          <span>Jobs&nbsp;</span>
          <RightOutlined className="text-xs" />
          <span>&nbsp;Add Job</span>
        </h3>
      </div>

      <div className="flex justify-center items-center mt-8">
        <div className="flex items-center">
          <div className="flex items-center justify-center w-6 h-6 rounded-full bg-blue-900 text-white text-xs">
            <CheckOutlined className="text-xs" />
          </div>
          <div className="w-60 h-1 bg-blue-950"></div>
          <div className="flex items-center justify-center w-6 h-6 rounded-full border border-gray-500 text-gray-500 text-sm font-semibold">
            2
          </div>
        </div>
      </div>

      <Form
        form={form}
        onFinish={handleSubmit}
        requiredMark="optional"
        layout="vertical"
        className="grid"
      >
        <div className="bg-white shadow-md rounded-md p-3 mt-6 w-full mx-auto">
          <div className="bg-white border border-blue rounded-md p-3 w-full mx-auto">
            <div className="grid gap-4 grid-cols-1 md:grid-cols-2 lg:grid-cols-2 mt-2">
              <div className="col-span-4">
                <Form.List name="skills">
                  {(fields, { add, remove }) => (
                    <>
                      <label className="block text-sm font-medium mb-2">
                        Skills & Competencies
                      </label>

                      {fields.map(({ key, name, ...restField }) => (
                        <div key={key} className="flex items-center gap-2 mb-2">
                          <Form.Item
                            {...restField}
                            name={[name, "name"]}
                            className="flex-1 mb-0"
                            rules={[
                              {
                                required: true,
                                message: "Please enter a skill",
                              },
                            ]}
                          >
                            <AutoComplete
                              options={jobTitleSkills?.map((skill: any) => ({
                                value: skill.name,
                              }))}
                              placeholder="Enter Competency"
                              onChange={(value) => {
                                handleSkillChange(name, value);
                              }}
                              filterOption={(inputValue, option) => {
                                if (!option?.value) return false;
                                return option.value
                                  .toLowerCase()
                                  .includes(inputValue.toLowerCase());
                              }}
                            />
                          </Form.Item>

                          <MinusCircleOutlined
                            className="text-red-500 cursor-pointer"
                            onClick={() => {
                              remove(name);
                              const currentSkills =
                                form.getFieldValue("skills") || [];
                              const updatedSkills = currentSkills.filter(
                                (_: any, index: number) => index !== name,
                              );
                              updateStep2({ skills: updatedSkills });
                              setSkillsManuallyEdited(true);
                            }}
                          />
                        </div>
                      ))}

                      <Button
                        type="dashed"
                        onClick={() => {
                          add();
                          setSkillsManuallyEdited(true);
                        }}
                        block
                        icon={<i className="ri-add-line" />}
                      >
                        Add Competency
                      </Button>
                    </>
                  )}
                </Form.List>
              </div>
            </div>
          </div>
          <div className="col-span-4 flex justify-between items-center mt-4">
            <div data-tour="back-job">
              <Button
                onClick={handleBack}
                className="mr-2 bg-white text-blue-900 rounded border border-gray-500 p-1.5 px-8"
              >
                Back
              </Button>
              <Button onClick={handleCancel}>Cancel</Button>
            </div>
            <div data-tour="submit-job">
              <AppButton label="Preview" type="submit" />
            </div>
          </div>

          <WarningModal
            open={isCancelModalVisible}
            title="Cancel progress"
            content={<>Once cancelled, progress would not be saved.</>}
            onConfirm={handleConfirmCancel}
            onCancel={() => setCancelModalVisible(false)}
            confirmText="Cancel"
            cancelText="Back"
          />
        </div>
      </Form>
    </DashboardLayout>
  );
};
